import type { MetricRenderAdapter } from "@/metrics/runtime-adapters"
import type { RunOutlier, RunOutlierMetricSample } from "@/runs"

export interface OutlierEvidence {
  direction: "low" | "high"
  fence: number | null
  metricId: string
  metricLabel: string
  metricVersionNumber: number | null
  severity: "mild" | "extreme"
  value: number
}

export type OutlierEvidenceBySimulationId = Map<string, OutlierEvidence[]>

const EMPTY_OUTLIER_EVIDENCE: OutlierEvidenceBySimulationId = new Map()

function hasSameMetricVersion(
  outlier: RunOutlier,
  metricSample: RunOutlierMetricSample,
): boolean {
  return (
    outlier.metric_id === metricSample.metric_id &&
    (outlier.metric_version_id ?? null) ===
      (metricSample.metric_version_id ?? null) &&
    (outlier.metric_version_number ?? null) ===
      (metricSample.metric_version_number ?? null)
  )
}

function getFence(
  outlier: RunOutlier,
  metricSample: RunOutlierMetricSample | undefined,
): number | null {
  if (!metricSample) {
    return null
  }

  if (outlier.severity === "extreme") {
    return outlier.direction === "low"
      ? (metricSample.outer_low_fence ?? null)
      : (metricSample.outer_high_fence ?? null)
  }

  return outlier.direction === "low"
    ? (metricSample.inner_low_fence ?? null)
    : (metricSample.inner_high_fence ?? null)
}

export function buildOutlierEvidence(
  outliers: readonly RunOutlier[],
  metricSamples: readonly RunOutlierMetricSample[],
  metricRenderAdaptersById: Record<string, MetricRenderAdapter>,
): OutlierEvidenceBySimulationId {
  const metricLabelsById = new Map<string, string>()
  for (const metric of Object.values(metricRenderAdaptersById)) {
    metricLabelsById.set(metric.id, metric.metric_display_name)
  }

  const evidenceBySimulationId: OutlierEvidenceBySimulationId = new Map()
  for (const outlier of outliers) {
    const metricSample = metricSamples.find((sample) =>
      hasSameMetricVersion(outlier, sample),
    )
    const evidence: OutlierEvidence = {
      direction: outlier.direction,
      fence: getFence(outlier, metricSample),
      metricId: outlier.metric_id,
      metricLabel: metricLabelsById.get(outlier.metric_id) ?? outlier.metric_id,
      metricVersionNumber: outlier.metric_version_number ?? null,
      severity: outlier.severity,
      value: outlier.value,
    }
    const existingEvidence = evidenceBySimulationId.get(
      outlier.simulation_output_id,
    )
    if (existingEvidence) {
      existingEvidence.push(evidence)
    } else {
      evidenceBySimulationId.set(outlier.simulation_output_id, [evidence])
    }
  }

  return evidenceBySimulationId
}

export function getOutlierEvidenceBySimulationId(
  enabled: boolean,
  outliers: readonly RunOutlier[],
  metricSamples: readonly RunOutlierMetricSample[],
  metricRenderAdaptersById: Record<string, MetricRenderAdapter>,
): OutlierEvidenceBySimulationId {
  return enabled
    ? buildOutlierEvidence(outliers, metricSamples, metricRenderAdaptersById)
    : EMPTY_OUTLIER_EVIDENCE
}
